import { memo } from "react"
import {
  MoreHorizontal,
  Eye,
  Pen,
  Download,
  Copy,
  Archive,
  Trash2,
} from "lucide-react"
import { Menu, MenuTrigger, MenuPopup, MenuItem, MenuSeparator } from "@/components/ui/menu"
import type { DemoDocument } from "@/types/demo.types"

interface DocumentActionsMenuProps {
  doc: DemoDocument
  /**
   * Real-file action callbacks (prod path). Demo mode passes none, so the
   * menu keeps its existing read-only behaviour. View opens the linked
   * contract analysis; Download streams the file's presigned URL. Edit /
   * Duplicate / Archive / Delete have no backend verb yet — they render
   * disabled rather than as dead clicks.
   */
  onView?: () => void
  onDownload?: () => void
  /** True only when there's a linked contract analysis to view. */
  canView?: boolean
  /** True when this is a real deal file (enables Download, disables unbuilt actions). */
  isRealFile?: boolean
}

export const DocumentActionsMenu = memo(
  ({ doc, onView, onDownload, canView = false, isRealFile = false }: DocumentActionsMenuProps) => {
    // Edit / Duplicate / Archive / Delete need backend verbs that don't exist
    // yet. On a real file, honestly disable them instead of wiring dead clicks.
    // Demo docs keep the existing (non-disabled, no-op) menu so the demo tour
    // visual is unchanged.
    const unbuiltDisabled = isRealFile

    return (
      <Menu>
        <MenuTrigger
          className="p-2 rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Actions for ${doc.name}`}
        >
          <MoreHorizontal className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        </MenuTrigger>
        <MenuPopup align="end" sideOffset={4}>
          <MenuItem disabled={isRealFile && !canView} onClick={onView}>
            <Eye className="w-4 h-4" />
            View
          </MenuItem>
          <MenuItem disabled={unbuiltDisabled}>
            <Pen className="w-4 h-4" />
            Edit
          </MenuItem>
          <MenuItem disabled={isRealFile && !onDownload} onClick={onDownload}>
            <Download className="w-4 h-4" />
            Download
          </MenuItem>
          <MenuItem disabled={unbuiltDisabled}>
            <Copy className="w-4 h-4" />
            Duplicate
          </MenuItem>
          <MenuSeparator />
          <MenuItem disabled={unbuiltDisabled}>
            <Archive className="w-4 h-4" />
            Archive
          </MenuItem>
          <MenuItem variant="destructive" disabled={unbuiltDisabled}>
            <Trash2 className="w-4 h-4" />
            Delete
          </MenuItem>
        </MenuPopup>
      </Menu>
    )
  },
)

DocumentActionsMenu.displayName = "DocumentActionsMenu"
